import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useGet } from "../../hooks/api";
import { apiUrls } from "../../utils/urls";
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  ListSubheader,
  Paper,
  Box,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getMostRecentSunday } from "../../utils/calendar";
import { Stack } from "@mui/system";
import { sum } from "../../utils/helpers";
import WeekStatsSkeleton from "./WeekStatsSkeleton";

export default function WeekStats({
  userProfileId,
  startDate,
  endDate,
  embeddedMode = false,
  refetch: refetchWeekStats = false,
  setRefetch: setRefetchWeekStats = null,
}) {
  // State for the start of the week (most recent Sunday by default or provided startDate)
  const [date, setDate] = useState(startDate ? dayjs(startDate) : dayjs());
  // Constant for today's most recent Sunday (prevents navigating into future)
  const mostRecentSunday = getMostRecentSunday(date);
  const actualMostRecentSunday = getMostRecentSunday();
  const isCurrentWeek = startDate
    ? false
    : date.isSame(actualMostRecentSunday, "week");

  // Effective date range (use provided props if available; otherwise current week)
  const effectiveStart = startDate ? dayjs(startDate) : mostRecentSunday;
  const effectiveEnd = endDate ? dayjs(endDate) : effectiveStart.add(6, "day");

  // Handlers to navigate weeks (only relevant when not embedded)
  const prevWeek = () => setDate(mostRecentSunday.subtract(7, "day"));
  const nextWeek = () => setDate(mostRecentSunday.add(7, "day"));

  // Format display for start and end of week
  const startLabel = effectiveStart.format("M/D/YY");
  const endLabel = effectiveEnd.format("M/D/YY");

  // Build search params
  const searchParams =
    startDate || endDate
      ? {
          start_date: effectiveStart.format("YYYY-MM-DD"),
          end_date: effectiveEnd.format("YYYY-MM-DD"),
        }
      : {
          start_date: mostRecentSunday.format("YYYY-MM-DD"),
          end_date: mostRecentSunday.add(6, "day").format("YYYY-MM-DD"),
        };
  if (userProfileId) {
    searchParams.user_profile_id = userProfileId;
  }

  // Fetch week stats
  const { data, isLoading, refetch } = useGet(apiUrls.admin.weekStats(), {
    params: {
      exclude: ["user_profile"],
      order_by: ["-date"],
      search_params: searchParams,
    },
  });

  const totalHours = sum(
    (data?.data || []).map(({ hours_worked }) => parseFloat(hours_worked ?? 0))
  ).toFixed(2);
  useEffect(() => {
    if (refetchWeekStats && setRefetchWeekStats) {
      refetch();
      setRefetchWeekStats(false);
    }
  }, [refetchWeekStats, setRefetchWeekStats, refetch]);

  // Render skeleton while loading
  if (isLoading) {
    return (
      <WeekStatsSkeleton
        startLabel={startLabel}
        endLabel={endLabel}
        embeddedMode={embeddedMode}
        prevWeek={prevWeek}
        nextWeek={nextWeek}
        isCurrentWeek={isCurrentWeek}
      />
    );
  }

  // Render actual data when loaded
  return (
    <List
      sx={{ height: "100%", overflowY: "auto" }}
      dense
      subheader={
        <ListSubheader
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            width: "100%",
            py: 1,
          })}
          component={Paper}
          elevation={0}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={2}
            sx={{ width: "100%" }}
            justifyContent={"space-between"}
          >
            <Typography variant="h6">Week Stats</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {!embeddedMode && (
                <IconButton size="small" onClick={prevWeek}>
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
              )}

              <Typography
                variant="body1"
                sx={(theme) => ({ color: theme.palette.text.primary })}
              >
                {startLabel} - {endLabel}
              </Typography>

              {!embeddedMode && (
                <IconButton
                  size="small"
                  onClick={nextWeek}
                  disabled={isCurrentWeek}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Stack>
          <Typography
            variant="subtitle2"
            fontSize={"small"}
            sx={{ width: "100%", textAlign: "right" }}
          >
            Total Hours: {totalHours}
          </Typography>
        </ListSubheader>
      }
    >
      {(data?.data || []).map((day, idx) => {
        const {
          date: dayDate,
          checked_in,
          checked_in_on_time,
          hours_worked,
          time_submission_dt,
          todo_completions,
          schedule_item_completions,
        } = day;
        return (
          <React.Fragment key={dayDate}>
            <ListItem disableGutters>
              <ListItemText
                primary={dayjs(dayDate).format("dddd, M/D")}
                slotProps={{
                  primary: {
                    variant: "body2",
                    sx: { fontWeight: 500, color: "text.primary" },
                  },
                }}
                disableTypography
                secondary={
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Hours: {parseFloat(hours_worked ?? 0).toFixed(2)}{" "}
                        {time_submission_dt && hours_worked ? (
                          <Box
                            component="span"
                            sx={{
                              color:
                                dayjs(time_submission_dt).format(
                                  "YYYY-MM-DD"
                                ) === dayjs(dayDate).format("YYYY-MM-DD")
                                  ? "success.main"
                                  : "warning.main",
                            }}
                          >
                            ({dayjs(time_submission_dt).format("M/D h:mm A")})
                          </Box>
                        ) : (
                          <Box component={"span"} color={"error.main"}>
                            (Unsubmitted)
                          </Box>
                        )}
                      </Typography>

                      <Box
                        sx={{
                          width: "2px",
                          height: "12px",
                          backgroundColor: "divider",
                          borderRadius: "1px",
                        }}
                      />

                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: checked_in ? "success.main" : "text.secondary",
                        }}
                      >
                        {checked_in ? "Checked In" : "Not Checked In"}
                      </Typography>

                      {checked_in && (
                        <>
                          <Box
                            sx={{
                              width: "2px",
                              height: "12px",
                              backgroundColor: "divider",
                              borderRadius: "1px",
                            }}
                          />

                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              color: checked_in_on_time
                                ? "success.main"
                                : "warning.main",
                            }}
                          >
                            {checked_in_on_time ? "On Time" : "Late"}
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        gap: 0.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        color="primary"
                        label={`To-dos Completed: ${todo_completions ?? 0}`}
                        sx={{
                          height: "20px",
                          fontSize: "0.7rem",
                          "& .MuiChip-label": {
                            px: 1,
                            py: 0.25,
                          },
                        }}
                      />
                      <Chip
                        size="small"
                        color="info"
                        label={`Schedule Items Completed: ${
                          schedule_item_completions ?? 0
                        }`}
                        sx={{
                          height: "20px",
                          fontSize: "0.7rem",
                          "& .MuiChip-label": {
                            px: 1,
                            py: 0.25,
                          },
                        }}
                      />
                    </Box>
                  </>
                }
              />
            </ListItem>
            {idx < (data?.data?.length || 0) - 1 && <Divider component="li" />}
          </React.Fragment>
        );
      })}
    </List>
  );
}
